import NeptuneElement from '../../NeptuneElement.js';
import KebabMenuItem from '../kebabMenu/KebabMenuItem/KebabMenuItem.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class KebabMenu extends i18nMixin(NeptuneElement) {
    static KEBAB_STATES = {
        primaryDrawer: 'primary-drawer',
        secondaryDrawer: 'secondary-drawer'
    };

    templateMarkup = `
        <nav>
            <button type="button" class="font-size-md back-button" hidden>
                <i class="fa-solid fa-chevron-left"></i>
                <span>Back</span>
            </button>
            <menu></menu>
            <menu hidden></menu>
        </nav>
    `;

    componentCssUrl = '/src/components/chrome/kebabMenu/kebab-menu.css';

    kebabItems = [];

    #activeParentLabel = null;
    #activeRoute = null;

    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
        if (culture) { i18n.setLocale(culture); }
        this.#applyTranslations();
        this.#renderPrimaryMenu();

        const state = this.getAttribute('state');
        const nav = this.shadowRoot.querySelector('nav');
        if (nav) {
            if (state) {nav.dataset.state = state;}
            else {delete nav.dataset.state;}
        }

        if (this.pendingKebabItems) {
            this.setKebabItems(this.pendingKebabItems);
            this.pendingKebabItems = null;
        }
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    onLocaleChange() {
        this.#applyTranslations();
    }

    static get observedAttributes() {
        return ['state', 'culture'];
    }

    async setEventListeners() {
        await this.removeEventListeners();

        this.backButton = this.shadowRoot.querySelector('nav > button.back-button');

        this.handleGoBackBound = this.#goBackToDrawer.bind(this);
        this.handleItemToggleBound = this.#handleItemToggle.bind(this);
        this.handleItemClickBound = this.#handleItemClick.bind(this);
        this.handleLocationChangeBound = this.#handleLocationChange.bind(this);

        this.backButton?.addEventListener('click', this.handleGoBackBound);
        this.addEventListener('neptune-kebab-menu-item-toggle', this.handleItemToggleBound);
        this.addEventListener('neptune-kebab-menu-item-click', this.handleItemClickBound);
        window.addEventListener('neptune-location-change', this.handleLocationChangeBound);
        window.addEventListener('popstate', this.handleLocationChangeBound);
    }

    removeEventListeners() {
        if (this.backButton && this.handleGoBackBound) {
            this.backButton.removeEventListener('click', this.handleGoBackBound);
        }
        if (this.handleItemToggleBound) {
            this.removeEventListener('neptune-kebab-menu-item-toggle', this.handleItemToggleBound);
        }
        if (this.handleItemClickBound) {
            this.removeEventListener('neptune-kebab-menu-item-click', this.handleItemClickBound);
        }
        if (this.handleLocationChangeBound) {
            window.removeEventListener('neptune-location-change', this.handleLocationChangeBound);
            window.removeEventListener('popstate', this.handleLocationChangeBound);
        }
        this.backButton = null;
        this.handleGoBackBound = null;
        this.handleItemToggleBound = null;
        this.handleItemClickBound = null;
        this.handleLocationChangeBound = null;
    }

    /**
     * Lifecycle callback invoked when an observed attribute changes.
     * Ignores changes before initial render.
     * @param {string} name - The attribute name.
     * @param {?string} oldValue - The previous attribute value.
     * @param {?string} newValue - The new attribute value.
     * @returns {void}
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {return;}
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Processes an attribute change and updates the component's state.
     * Currently handles only the `state` attribute.
     * @param {string} name - The attribute name.
     * @param {?string} oldValue - The previous attribute value.
     * @param {?string} newValue - The new attribute value.
     * @returns {void}
     */
    processAttribute(name, oldValue, newValue) {
        if (name === 'culture') {
            if (newValue) {
                i18n.setLocale(newValue);
                this.#applyTranslations();
            }
            return;
        }
        if (name !== 'state' || !this.shadowRoot) {return;}
        const nav = this.shadowRoot.querySelector('nav');
        if (!nav) {return;}

        if (newValue) {nav.dataset.state = newValue;}
        else {delete nav.dataset.state;}

        const isSecond = newValue === KebabMenu.KEBAB_STATES.secondaryDrawer;
        this.#updateBackButton(isSecond);
        this.#updateMenuVisibility(isSecond);

        if (!isSecond) {
            this.shadowRoot.querySelectorAll('neptune-kebab-menu-item[expanded]')
                .forEach(el => el.removeAttribute('expanded'));
        }
    }

    /**
     * Updates the kebab menu items and re-renders the primary menu.
     * Skips update when the new data is deep-equal to the current data.
     * Normalizes non-array input to an empty array.
     * @param {Array<Object>} items - Array of kebab menu item objects.
     * @returns {void}
     */
    setKebabItems(items) {
        const normalized = Array.isArray(items) ? JSON.parse(JSON.stringify(items)) : [];
        if (JSON.stringify(this.kebabItems) === JSON.stringify(normalized)) {return;}
        this.kebabItems = normalized;
        if (this.isRendered) {this.#renderPrimaryMenu();}
    }

    /**
     * Opens the kebab menu in primary drawer state.
     * Emits a `neptune-kebab-menu-toggle` event with detail `{ state: 'primary-drawer' }`.
     * @returns {void}
     */
    open() {
        this.setAttribute('state', KebabMenu.KEBAB_STATES.primaryDrawer);
        this.#emitToggle(KebabMenu.KEBAB_STATES.primaryDrawer);
    }

    /**
     * Closes the kebab menu by removing the `state` attribute entirely.
     * Emits a `neptune-kebab-menu-toggle` event with detail `{ state: null }`.
     * Safe to call when already closed.
     * @returns {void}
     */
    close() {
        if (this.hasAttribute('state')) {this.removeAttribute('state');}
        this.#emitToggle(null);
    }

    /**
     * Reports whether the kebab menu is currently open.
     * @returns {boolean} True if the `state` attribute is present, false otherwise.
     */
    isOpen() {
        return this.hasAttribute('state');
    }

    /**
     * Clears the active highlight from all menu items and resets internal state.
     * @returns {void}
     */
    clearActive() {
        this.#activeRoute = null;
        this.#allItems().forEach(el => {
            el.removeAttribute('active');
            el.setAttribute('state', 'default');
        });
    }

    /**
     * Sets the active route and highlights the matching item(s).
     * If the route matches a submenu child, the parent item is also highlighted.
     * Passing null or empty string clears all active highlights.
     * @param {?string} route - The route to activate.
     * @returns {void}
     */
    setActiveRoute(route) {
        if (!route) {
            this.clearActive();
            return;
        }
        this.#activeRoute = route;
        const normalized = this.#normalizeRoute(route);

        this.#allItems().forEach(item => {
            const match = this.#normalizeRoute(item.getAttribute('route')) === normalized;
            item.toggleAttribute('active', match);
            item.setAttribute('state', match ? 'active' : 'default');
        });

        const parent = this.kebabItems.find(item =>
            item.subMenu?.some(sub => this.#normalizeRoute(sub.route) === normalized)
        );
        if (parent) {
            this.#activeParentLabel = parent.label || parent.name;
            const parentEl = this.shadowRoot?.querySelector(
                `menu:first-of-type neptune-kebab-menu-item[label="${this.#activeParentLabel}"]`
            );
            if (parentEl) {
                parentEl.setAttribute('active', '');
                parentEl.setAttribute('state', 'active');
            }
        }
    }

    /**
     * Retrieves all kebab menu item elements currently in the shadow DOM.
     * @returns {HTMLElement[]}
     */
    #allItems() {
        return [...(this.shadowRoot?.querySelectorAll('neptune-kebab-menu-item') ?? [])];
    }

    /**
     * Normalizes a route by stripping a leading hash character.
     * Enables matching for hash-based routes.
     * @param {?string} route - The route to normalize.
     * @returns {string} The normalized route, or empty string if input is null/undefined.
     */
    #normalizeRoute(route) {
        return route?.replace(/^#/, '') ?? '';
    }

    /**
     * Applies translated content for the kebab menu component.
     * @returns {void}
     */
    #applyTranslations() {
        const backLabel = this.shadowRoot?.querySelector('nav > button.back-button span');
        if (backLabel) {
            backLabel.textContent = this.translate('KEBAB_MENU.BACK');
        }
    }

    /**
     * Toggles visibility of the back button based on drawer state.
     * @param {boolean} isSecond - True when in secondary-drawer state.
     * @returns {void}
     */
    #updateBackButton(isSecond) {
        const backBtn = this.shadowRoot.querySelector('nav > button.back-button');
        if (!backBtn) {return;}
        backBtn.hidden = !isSecond;
    }

    /**
     * Toggles visibility of primary vs secondary menu based on drawer state.
     * @param {boolean} isSecond - True when in secondary-drawer state.
     * @returns {void}
     */
    #updateMenuVisibility(isSecond) {
        const primaryMenu = this.shadowRoot.querySelector('menu:first-of-type');
        const secondaryMenu = this.shadowRoot.querySelector('menu:last-of-type');
        if (primaryMenu) {primaryMenu.hidden = isSecond;}
        if (secondaryMenu) {secondaryMenu.hidden = !isSecond;}
    }

    /**
     * Handles a menu item toggle event.
     * Opens the secondary drawer when a parent item with a submenu is expanded.
     * @param {CustomEvent} event - The neptune-kebab-menu-item-toggle event.
     * @returns {void}
     */
    #handleItemToggle(event) {
        const { expanded, label } = event.detail;
        if (!expanded) {return;}
        const item = this.#findKebabItem(label);
        if (item?.subMenu?.length) {
            this.#activeParentLabel = label;
            this.#openSecondary(item.subMenu);
        }
    }

    /**
     * Handles a menu item click event.
     * Highlights the clicked item, closes the drawer, and emits a navigate event.
     * @param {CustomEvent} event - The neptune-kebab-menu-item-click event.
     * @returns {void}
     */
    #handleItemClick(event) {
        this.setActiveRoute(event.detail.route);
        this.close();
        this.dispatchEvent(new CustomEvent('neptune-kebab-menu-navigate', {
            bubbles: true,
            composed: true,
            detail: {
                id: event.detail.id,
                route: event.detail.route,
                label: event.detail.label
            }
        }));
        
        window.dispatchEvent(new CustomEvent('neptune-location-change', {
            detail: { route: event.detail.route }
        }));

    }

    /**
     * Handles a location change event from history navigation or popstate.
     * Updates the active route if the new route differs from the current one.
     * Falls back to window.location.pathname when the event has no detail.
     * @param {Event|CustomEvent} event - The neptune-location-change or popstate event.
     * @returns {void}
     */
    #handleLocationChange(event) {
        const newRoute = this.#normalizeRoute(event?.detail?.route ?? window.location.pathname);
        if (this.#activeRoute && newRoute === this.#normalizeRoute(this.#activeRoute)) {return;}
        this.setActiveRoute(newRoute);
    }

    /**
     * Populates the secondary menu and transitions to secondary-drawer state.
     * @param {Array<Object>} subItems - Array of submenu item objects.
     * @returns {void}
     */
    #openSecondary(subItems) {
        this.#populateSecondaryMenu(subItems);
        this.setAttribute('state', KebabMenu.KEBAB_STATES.secondaryDrawer);
        this.#emitToggle(KebabMenu.KEBAB_STATES.secondaryDrawer);
    }

    /**
     * Renders submenu items into the secondary menu container.
     * Preserves active state if the submenu contains the current active route.
     * @param {Array<Object>} subItems - Array of submenu item objects.
     * @returns {void}
     */
    #populateSecondaryMenu(subItems) {
        const menu = this.shadowRoot.querySelector('menu:last-of-type');
        if (!menu) {return;}
        menu.innerHTML = '';
        subItems.forEach(item => {
            const el = this.#createKebabItem(item);
            if (this.#activeRoute && el.getAttribute('route') === this.#activeRoute) {
                el.setAttribute('active', '');
                el.setAttribute('state', 'active');
            }
            menu.appendChild(el);
        });
    }

    /**
     * Returns from secondary drawer to primary drawer.
     * Collapses any expanded items and emits a toggle event.
     * @returns {void}
     */
    #goBackToDrawer() {
        this.shadowRoot.querySelectorAll('neptune-kebab-menu-item[expanded]')
            .forEach(el => el.removeAttribute('expanded'));
        this.setAttribute('state', KebabMenu.KEBAB_STATES.primaryDrawer);
        this.#emitToggle(KebabMenu.KEBAB_STATES.primaryDrawer);
    }

    /**
     * Renders top-level menu items from menuData into the primary menu container.
     * Clears any existing content first.
     * @returns {void}
     */
    #renderPrimaryMenu() {
        const menu = this.shadowRoot?.querySelector('menu:first-of-type');
        if (!menu) {return;}
        menu.innerHTML = '';
        this.kebabItems.forEach(item => menu.appendChild(this.#createKebabItem(item)));
    }

    /**
     * Creates a `<neptune-kebab-menu-item>` element from an item data object.
     * @param {Object} item - The menu item data.
     * @param {Array<Object>} [item.subMenu] - Submenu children; when non-empty, adds `submenu` attribute.
     * @param {boolean} [item.isActive] - When true, item renders with active attribute.
     * @returns {HTMLElement} A configured neptune-kebab-menu-item element.
     */
    #createKebabItem(item) {
        const el = document.createElement('neptune-kebab-menu-item');
        const label = item.label || item.name || '';
        el.setAttribute('label', label);
        el.setAttribute('route', item.route || '#');
        if (item.icon) {el.setAttribute('icon', item.icon);}
        if (item.avatarText) {el.setAttribute('avatarText', item.avatarText);}
        if (item.id) {el.setAttribute('data-item-id', item.id);}
        if (item.subMenu?.length) {el.setAttribute('submenu', '');}
        if (item.isActive) {el.setAttribute('active', '');}
        return el;
    }

    /**
     * Finds a top-level menu item by its label or name.
     * @param {string} label - The label to search for.
     * @returns {?Object} The matching item data, or null if not found.
     */
    #findKebabItem(label) {
        return this.kebabItems.find(item => (item.label || item.name) === label) || null;
    }

    /**
     * Emits a `neptune-kebab-menu-toggle` event.
     * @param {?string} state - The new drawer state, or null when closed.
     * @returns {void}
     */
    #emitToggle(state) {
        this.dispatchEvent(new CustomEvent('neptune-kebab-menu-toggle', {
            bubbles: true,
            composed: true,
            detail: { state }
        }));
    }
}

if (!customElements.get('neptune-kebab-menu')) {
    customElements.define('neptune-kebab-menu', KebabMenu);
}

export default KebabMenu;